import type { ChatFileAttachment } from "@v2/features/chat/utils/chatFileAttachment";
import type { SessionPromptFile } from "schemas/generated/agentConnection";
import type { SessionMessageWithParts } from "schemas/generated/opencode";

import { useCallback } from "react";

import { useSessionUuid } from "@agent-ui/features/chat/hooks/useSessionUuid";
import { useSessionStoreActions } from "@agent-ui/features/chat/store/SessionStoreContext";
import { batchUploadToCloudflare } from "@agent-ui/features/chat/utils/uploadChatFiles";
import { useCurrentFile } from "@agent-ui/features/page-renderer/hooks/useCurrentFile";
import { toast } from "@replo/design-system/components/toast/Toast";
import { useMutation } from "@tanstack/react-query";
import { useReploAgent } from "@v2/contexts/ReploAgentContext";
import { trackError } from "@v2/features/infra/analytics/tracking";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useSearchParams } from "react-router-dom";
import { filterNulls } from "replo-utils/lib/array";
import { ReploError } from "schemas/errors";

class SessionNotRegisteredError extends ReploError {}

function createOptimisticAssistantMessage({
  sessionUuid,
}: {
  sessionUuid: string;
}): SessionMessageWithParts {
  const now = new Date();
  return {
    message: {
      id: `optimistic-assistant-${sessionUuid}`,
      userId: "",
      opencodeSessionUuid: sessionUuid,
      createdAt: new Date(now.getTime() + 1),
      updatedAt: new Date(now.getTime() + 1),
      deletedAt: null,
      opencodeId: `optimistic-assistant-${sessionUuid}`,
      opencodeSessionId: "",
      opencodeParentId: null,
      completedAt: null,
      role: "assistant",
      finishReason: null,
      error: null,
      cost: null,
      tokensInput: null,
      tokensOutput: null,
      tokensReasoning: null,
    },
    parts: [],
  };
}

function createOptimisticUserMessage({
  sessionUuid,
  text,
  files,
}: {
  sessionUuid: string;
  text: string;
  files: ChatFileAttachment[];
}): SessionMessageWithParts {
  const now = new Date();
  const messageId = `optimistic-${sessionUuid}`;

  const fileParts = files
    .filter((attachment) => {
      return (
        attachment.file.type.startsWith("image/") ||
        attachment.file.type.startsWith("video/")
      );
    })
    .map((attachment, index) => ({
      id: `optimistic-file-part-${sessionUuid}-${String(index)}`,
      opencodeMessageUuid: messageId,
      opencodeId: `optimistic-file-part-${sessionUuid}-${String(index)}`,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
      partData: {
        id: `optimistic-file-part-${sessionUuid}-${String(index)}`,
        sessionID: "",
        messageID: messageId,
        type: "file" as const,
        mime: attachment.file.type,
        filename: attachment.file.name,
        url: URL.createObjectURL(attachment.file),
      },
    }));

  return {
    message: {
      id: messageId,
      userId: "",
      opencodeSessionUuid: sessionUuid,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
      opencodeId: messageId,
      opencodeSessionId: "",
      opencodeParentId: null,
      completedAt: null,
      role: "user",
      finishReason: null,
      error: null,
      cost: null,
      tokensInput: null,
      tokensOutput: null,
      tokensReasoning: null,
    },
    parts: [
      ...fileParts,
      {
        id: `optimistic-part-${sessionUuid}`,
        opencodeMessageUuid: messageId,
        opencodeId: `optimistic-part-${sessionUuid}`,
        createdAt: now,
        updatedAt: now,
        deletedAt: null,
        partData: {
          id: `optimistic-part-${sessionUuid}`,
          sessionID: "",
          messageID: messageId,
          type: "text",
          text,
        },
      },
    ],
  };
}

/**
 * Use in chat/home interaction surfaces that create sessions or send prompts.
 * It wraps ReploAgent actions with SessionStore bookkeeping so the UI can
 * route events by `sessionUuid` and reflect per-session streaming state.
 */
export function useSessionActions() {
  const { actions: agentActions } = useReploAgent();
  const {
    registerSession,
    setStreaming,
    getAgentSessionId,
    setOptimisticMessages,
  } = useSessionStoreActions();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageRouteParam = searchParams.get("pageRoute");
  const currentSessionUuid = useSessionUuid();
  const projectId = useCurrentProjectId();

  // Get current page for context
  // NOTE (Yuxin, 2026-01-31): Fall back to pageRouteParam for sandbox pages not in database
  const currentFile = useCurrentFile();
  const currentPageRoute = currentFile?.pageRoute ?? pageRouteParam;

  const createSessionMutation = useMutation({
    mutationFn: async ({ sessionUuid }: { sessionUuid?: string } = {}) => {
      // NOTE (Noah, 2026-02-22): Generate UUID on the frontend so we can set the
      // sessionUuid in the URL immediately. This allows sandbox-status messages
      // (which arrive during the sessionCreate RPC) to be displayed in real time.
      const resolvedUuid = sessionUuid ?? crypto.randomUUID();

      setSearchParams((previousSearchParams) => {
        const nextSearchParams = new URLSearchParams(previousSearchParams);
        nextSearchParams.set("sessionUuid", resolvedUuid);
        return nextSearchParams;
      });

      const result = await agentActions.sessionCreate({
        sessionUuid: resolvedUuid,
      });
      return result;
    },
    onSuccess: (result) => {
      registerSession({
        sessionUuid: result.sessionUuid,
        agentSessionId: result.sessionId,
      });
    },
  });

  const createSession = useCallback(
    async ({ sessionUuid }: { sessionUuid?: string } = {}) => {
      return createSessionMutation.mutateAsync({ sessionUuid });
    },
    [createSessionMutation],
  );

  const sendPromptMutation = useMutation({
    mutationFn: async ({
      prompt,
      files,
    }: {
      prompt: string;
      files: ChatFileAttachment[];
    }) => {
      const sessionUuid = currentSessionUuid
        ? currentSessionUuid
        : crypto.randomUUID();

      setStreaming({ sessionUuid, isStreaming: true });
      setOptimisticMessages({
        sessionUuid,
        messages: [
          createOptimisticUserMessage({ sessionUuid, text: prompt, files }),
          createOptimisticAssistantMessage({ sessionUuid }),
        ],
      });

      try {
        if (!currentSessionUuid) {
          await createSession({ sessionUuid });
        }

        const sessionId = getAgentSessionId({ sessionUuid });
        if (!sessionId) {
          throw new SessionNotRegisteredError({
            message: "SessionId is not registered",
            additionalData: { sessionUuid },
          });
        }

        let uploadedFiles: SessionPromptFile[] = [];
        const fileContextLines: string[] = [];

        if (files.length > 0 && projectId) {
          try {
            const uploadedFileResults = await batchUploadToCloudflare(
              files.map((attachment) => attachment.file),
              projectId,
            );

            uploadedFiles = uploadedFileResults.map((uploadedFileResult) => ({
              url: uploadedFileResult.assetUrl,
              mime: uploadedFileResult.file.type,
              filename: uploadedFileResult.file.name,
            }));

            // NOTE (Max, 2026-03-10): Provide CDN URLs for attached files via
            // systemContext so the LLM knows where they're hosted and can use
            // them directly (e.g. in <img> or <video> tags) instead of trying
            // to download or recreate them locally.
            for (const result of uploadedFileResults) {
              if (result.file.type.startsWith("image/")) {
                fileContextLines.push(
                  `[Attached Image] This image is already hosted at: ${result.assetUrl} — use this URL directly in <img> tags, do not download or save locally.`,
                );
              } else if (result.file.type.startsWith("video/")) {
                fileContextLines.push(
                  `[Attached Video] This video is already hosted at: ${result.assetUrl} — use this URL directly in <video> tags, do not download or save locally.`,
                );
              }
            }
          } catch (error) {
            trackError(error, { context: "chat-file-upload" });
            toast.error({
              title: "Failed to upload attachments",
              description:
                "Your message will be sent without the attached files. Please try again.",
            });
          }
        }

        const pageContext = currentPageRoute
          ? `User is currently viewing route "${currentPageRoute}" in the editor.`
          : null;

        await agentActions.sessionPrompt({
          sessionId,
          prompt,
          sessionUuid,
          systemContext: filterNulls([pageContext, ...fileContextLines]).join(
            "\n",
          ),
          files: uploadedFiles,
        });
      } catch (error) {
        setStreaming({ sessionUuid, isStreaming: false });
        throw error;
      }
    },
  });

  const answerQuestionMutation = useMutation({
    mutationFn: async ({
      sessionUuid,
      questionId,
      answers,
    }: {
      sessionUuid: string;
      questionId: string;
      answers: string[][];
    }) => {
      setStreaming({ sessionUuid, isStreaming: true });
      await agentActions.answerQuestion({ sessionUuid, questionId, answers });
    },
  });

  const streamMessagesMutation = useMutation({
    mutationFn: async ({ sessionUuid }: { sessionUuid: string }) => {
      const sessionId = getAgentSessionId({ sessionUuid });
      if (!sessionId) {
        return;
      }

      await agentActions.streamMessages({
        sessionId,
        sessionUuid,
      });
    },
  });

  const abortSessionMutation = useMutation({
    mutationFn: async ({ sessionUuid }: { sessionUuid: string }) => {
      // NOTE (Max, 2026-02-26, REPL-23977): Optimistically stop streaming immediately
      // for responsive UI. The backend abort is best-effort — if it fails, the UI is
      // already in the correct stopped state. When the session.cancelled event arrives
      // from the backend, setStreaming(false) is called again (idempotent).
      setStreaming({ sessionUuid, isStreaming: false });
      await agentActions.sessionAbort({ sessionUuid });
    },
  });

  return {
    createSession: createSessionMutation.mutateAsync,
    isCreatingSession: createSessionMutation.isPending,
    sendPrompt: sendPromptMutation.mutateAsync,
    answerQuestion: answerQuestionMutation.mutateAsync,
    isAnsweringQuestion: answerQuestionMutation.isPending,
    streamMessages: streamMessagesMutation.mutateAsync,
    abortSession: abortSessionMutation.mutateAsync,
  };
}
